import { Href, redirect } from "@/features/i18n";
import { PropsWithChildren } from "react";
import { getSession } from "../cookies";
import { UserRoles } from "@/features/users";
import { ROUTES_ADMIN, ROUTES_USER } from "@/constants/routes";

export type ServerAuthGuardProps = PropsWithChildren & {
  /**
   * Modo de protección.
   * - `auth`: requiere sesión (por defecto).
   * - `guest`: requiere NO tener sesión.
   */
  require?: "auth" | "guest";

  /**
   * Restricción opcional por roles.
   * Si se provee, el usuario debe tener alguno de estos roles.
   */
  roles?: UserRoles[];

  /** A dónde redirigir si ya está autenticado. Por defecto `/dashboard`. */
  authenticatedRedirectTo?: Href;
};

/**
 * Guard de autenticación en servidor.
 *
 * Úsalo en layouts/pages de App Router cuando quieras una barrera real
 * (no se renderiza HTML para usuarios no autorizados).
 */
export async function ServerAuthGuard({
  children,
  require = "auth",
  roles,
  authenticatedRedirectTo = "/dashboard",
}: ServerAuthGuardProps) {
  const session = await getSession();

  if (require === "guest") {
    if (session) {
      await doRedirect(authenticatedRedirectTo);
      return null;
    }
    return children;
  }

  // require === "auth"
  if (!session) {
    // unauthorized();
    await doRedirect("/login");
    return;
  }

  if (roles && roles.length > 0) {
    const hasRole = roles.includes(session.role);
    if (!hasRole) {
      await doRedirect(getDefaultRouteByRole(session.role));
      return null;
    }
  }

  return children;
}

/**
 * Redirige usando la API tipada de next-intl en servidor.
 */
async function doRedirect(to: Href) {
  // La API tipada de next-intl en servidor espera un objeto con { href }.
  return redirect({ href: to });
}

function getDefaultRouteByRole(role: UserRoles): Href {
  const adminDefaultRoute = ROUTES_ADMIN[0]?.pathname;
  const userDefaultRoute = ROUTES_USER[0]?.pathname;

  if (role === UserRoles.ADMIN && adminDefaultRoute) {
    return adminDefaultRoute as Href;
  }

  if (role === UserRoles.USER && userDefaultRoute) {
    return userDefaultRoute as Href;
  }

  return "/dashboard";
}
